import { isGameLive } from './homeGameDisplayOrder';

/** Game ids seen as live this page session (survives popover remount). */
/** @type {Set<string>} */
const sessionLiveTouchedGameIds = new Set();

function normalizeLabel(s) {
  return String(s || '').trim().toLowerCase();
}

function labelsRoughlyMatch(a, b) {
  const x = normalizeLabel(a);
  const y = normalizeLabel(b);
  if (!x || !y) return false;
  if (x === y) return true;
  return x.includes(y) || y.includes(x);
}

function rowMatchesTeam(row, { tla, name }) {
  if (tla && row.tla && normalizeLabel(row.tla) === normalizeLabel(tla)) return true;
  if (name) {
    if (labelsRoughlyMatch(row.name, name) || labelsRoughlyMatch(row.shortName, name)) {
      return true;
    }
  }
  return false;
}

function findRowIndex(rows, team) {
  return rows.findIndex((row) => rowMatchesTeam(row, team));
}

function tableContainsBoth(rows, home, away) {
  return findRowIndex(rows, home) >= 0 && findRowIndex(rows, away) >= 0;
}

function pickTableIndex(snapshot, home, away) {
  const tables = snapshot?.tables;
  if (!Array.isArray(tables) || tables.length === 0) return -1;

  const grouped = [];
  for (let i = 0; i < tables.length; i += 1) {
    if (tables[i]?.group != null) grouped.push(i);
  }
  if (grouped.length > 0) {
    for (const i of grouped) {
      if (tableContainsBoth(tables[i].rows || [], home, away)) return i;
    }
    return -1;
  }

  const totalIdx = tables.findIndex((t) => t.type === 'TOTAL');
  return totalIdx >= 0 ? totalIdx : 0;
}

function cloneSnapshot(snapshot) {
  return JSON.parse(JSON.stringify(snapshot));
}

function compareStandingsRows(a, b) {
  if (b.points !== a.points) return b.points - a.points;
  if (b.goalDifference !== a.goalDifference) return b.goalDifference - a.goalDifference;
  if (b.goalsFor !== a.goalsFor) return b.goalsFor - a.goalsFor;
  return normalizeLabel(a.name).localeCompare(normalizeLabel(b.name));
}

function resortTable(rows) {
  const sorted = [...rows].sort(compareStandingsRows);
  return sorted.map((row, i) => ({ ...row, position: i + 1 }));
}

function applyResultToRow(row, goalsFor, goalsAgainst) {
  const next = { ...row };
  next.played = (next.played || 0) + 1;
  next.goalsFor = (next.goalsFor || 0) + goalsFor;
  next.goalsAgainst = (next.goalsAgainst || 0) + goalsAgainst;
  next.goalDifference = next.goalsFor - next.goalsAgainst;

  if (goalsFor > goalsAgainst) {
    next.won = (next.won || 0) + 1;
    next.points = (next.points || 0) + 3;
  } else if (goalsFor < goalsAgainst) {
    next.lost = (next.lost || 0) + 1;
  } else {
    next.drawn = (next.drawn || 0) + 1;
    next.points = (next.points || 0) + 1;
  }
  return next;
}

/**
 * Apply one overlay game onto a cloned snapshot (no appliedGameIds bookkeeping).
 * @returns {{ snapshot: object, applied: boolean }}
 */
function applyOverlayGame(snapshot, game) {
  if (!snapshot || !Array.isArray(snapshot.tables)) {
    return { snapshot, applied: false };
  }
  if (!game?.gameId || game.homeScore == null || game.awayScore == null) {
    return { snapshot, applied: false };
  }

  const home = { tla: game.homeTeamTla, name: game.homeTeam };
  const away = { tla: game.awayTeamTla, name: game.awayTeam };
  const tableIndex = pickTableIndex(snapshot, home, away);
  if (tableIndex < 0) {
    return { snapshot, applied: false };
  }

  const rows = [...(snapshot.tables[tableIndex].rows || [])];
  const homeIdx = findRowIndex(rows, home);
  const awayIdx = findRowIndex(rows, away);
  if (homeIdx < 0 || awayIdx < 0) {
    return { snapshot, applied: false };
  }

  const homeScore = Number(game.homeScore);
  const awayScore = Number(game.awayScore);
  rows[homeIdx] = applyResultToRow(rows[homeIdx], homeScore, awayScore);
  rows[awayIdx] = applyResultToRow(rows[awayIdx], awayScore, homeScore);
  const next = {
    ...snapshot,
    tables: snapshot.tables.map((t, i) =>
      i === tableIndex ? { ...t, rows: resortTable(rows) } : t
    )
  };
  return { snapshot: next, applied: true };
}

/**
 * Mark games currently live as session-touched so a later FINISHED still overlays.
 * @param {Array<object>|null|undefined} games
 */
export function noteLiveTouchedGames(games) {
  for (const g of games || []) {
    if (g?.gameId && isGameLive(g)) {
      sessionLiveTouchedGameIds.add(g.gameId);
    }
  }
}

/**
 * Games to overlay on the API base: live matches, or finished matches that were
 * live earlier this session (not finished-only-at-open).
 * @param {Array<object>|null|undefined} games
 * @returns {Array<object>}
 */
export function selectStandingsOverlays(games) {
  noteLiveTouchedGames(games);
  const overlays = [];
  for (const g of games || []) {
    if (!g?.gameId) continue;
    if (g.homeScore == null || g.awayScore == null) continue;
    if (isGameLive(g)) {
      overlays.push(g);
      continue;
    }
    if (g.status === 'finished' && sessionLiveTouchedGameIds.has(g.gameId)) {
      overlays.push(g);
    }
  }
  return overlays;
}

/**
 * Project display standings: clone API base, apply overlay games (live provisional
 * results and session-finished finals). Always starts from base so score changes replace.
 *
 * @param {object|null|undefined} baseSnapshot — API payload with `tables`
 * @param {Array<object>|null|undefined} overlayGames
 * @returns {object|null|undefined}
 */
export function projectLiveStandings(baseSnapshot, overlayGames) {
  if (!baseSnapshot || !Array.isArray(baseSnapshot.tables)) {
    return baseSnapshot;
  }

  const appliedIds = Array.isArray(baseSnapshot.appliedGameIds)
    ? baseSnapshot.appliedGameIds
    : [];

  let next = cloneSnapshot(baseSnapshot);
  for (const game of overlayGames || []) {
    if (!game?.gameId) continue;
    if (appliedIds.includes(game.gameId)) continue;
    const result = applyOverlayGame(next, game);
    next = result.snapshot;
  }
  return next;
}

/** @internal test helper */
export function resetLiveStandingsSessionForTests() {
  sessionLiveTouchedGameIds.clear();
}
